import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import { ActivityCard } from '@adsk/alloy-react-activity-log';
import { useLocale } from '@adsk/alloy-react-locale';
import StatusColorIndicator from '@adsk/alloy-react-status-color-indicator';
import Theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { useCostContext } from './CostProvider';
import { ACTIVITY_VARIANTS } from '../../constants';
import {
  getStatus,
  STATUS_COLOR_MAPPING,
  COST_TYPE,
  shouldShowStatus,
  MAPPING_KEY,
} from '../helpers/consts';
import { getDeepLink } from '../helpers/formatEntities';
import { customizedType } from '../helpers/localeHelper';

const StyledContentContainer = styled.div`
  display: flex;
  align-items: center;
  ${Theme.typography.bodyMediumBold}
  ${Theme.typography.ellipsis}
`;

const StyledStatusContainer = styled.div`
  ${Theme.typography.heading5};
  ${Theme.typography.ellipsis};
  display: flex;
  align-items: center;
  margin-top: 6px;
`;

const StyledStatusColorIndicator = styled(StatusColorIndicator)`
  height: 16px;
  width: 3px;
  cursor: default;
`;

const CostActivityCard = ({ entity, variant, type = '', ...rest }) => {
  const { name } = entity;
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const project = useCurrentProject();
  const { terminologies } = useCostContext();

  return (
    <ActivityCard
      {...rest}
      title={
        variant === ACTIVITY_VARIANTS.REMOVED
          ? T.translate('ACTIVITY_TITLE_REMOVED', {
              type: customizedType(T, type, terminologies),
            })
          : T.translate('ACTIVITY_TITLE_ADDED', {
              type: customizedType(T, type, terminologies),
            })
      }
    >
      <StyledContentContainer>
        <A
          style={{ marginRight: '5px' }}
          target="__blank"
          href={getDeepLink(entity, project, type)}
        >
          {entity[MAPPING_KEY[type]]}
        </A>
        <OverflowTooltip
          content={name}
          singleLine
          getTargetProps={() => ({
            style: { overflow: 'hidden', flex: 1 },
          })}
        >
          {name}
        </OverflowTooltip>
      </StyledContentContainer>

      {shouldShowStatus(type) && (
        <StyledStatusContainer>
          <StyledStatusColorIndicator
            statusColor={STATUS_COLOR_MAPPING[getStatus(type, entity)]}
          />
          <span style={{ marginLeft: '5px' }}>
            {getStatus(type, entity)
              ? T.translate(getStatus(type, entity).toUpperCase())
              : null}
          </span>
        </StyledStatusContainer>
      )}
    </ActivityCard>
  );
};

CostActivityCard.propTypes = {
  /**
   * The  entity we need to render.
   */
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    number: PropTypes.string,
    costStatus: PropTypes.string,
    budgetStatus: PropTypes.string,
  }).isRequired,
  /**
   * The activity title variant,should be one of 'removed' and 'added'
   */
  variant: PropTypes.oneOf(Object.values(ACTIVITY_VARIANTS)).isRequired,
  /**
   * The cost type, should be one of 'pco', 'oco', 'sco', 'rfq', 'rco','budget'
   */
  type: PropTypes.oneOf(COST_TYPE).isRequired,
};

export default CostActivityCard;
